import { Col, Row, Spinner } from 'react-bootstrap';
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import TableRowAction from 'components/common/TableRowAction';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import { useParams } from 'react-router-dom';
import AddMosqueModal from './AddMosqueModal';
import AddReportModal from './AddReportModal';
import { permissionsKeyMap } from 'routes/permissions/permissionKeys';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { supabase } from 'lib/supabase';
import { toast } from 'react-toastify';
import {
  useBeneficiaryMosques,
  useCreateMosqueOrphan,
  useDeleteMosqueOrphan,
  useMosqueReports,
  useCreateMosqueReport,
  useUpdateMosqueReport,
  useDeleteMosqueReport
} from 'hooks/useBeneficiaries';
import useUpload from 'hooks/useUpload';
import { useAuthContext } from 'context/authContext';

// Component to handle document viewing with signed URLs
const ReportDocumentLink = ({ url, name }) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      let filePath = '';
      if (url.includes('/storage/v1/object/public/documents/')) {
        filePath = url.split('/storage/v1/object/public/documents/')[1];
      } else if (url.includes('/storage/v1/object/sign/documents/')) {
        filePath = url
          .split('/storage/v1/object/sign/documents/')[1]
          .split('?')[0];
      } else {
        window.open(url, '_blank');
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(filePath, 3600);

      if (error) {
        console.error('Error creating signed URL:', error);
        toast.error('Failed to open document');
        setLoading(false);
        return;
      }

      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error opening document:', err);
      toast.error('Failed to open document');
    }
    setLoading(false);
  };

  return (
    <a
      href="#"
      onClick={handleClick}
      style={{ cursor: loading ? 'wait' : 'pointer' }}
    >
      {loading ? (
        <Spinner size="sm" animation="border" />
      ) : (
        <FontAwesomeIcon icon="link" />
      )}{' '}
      {name}
    </a>
  );
};

ReportDocumentLink.propTypes = {
  url: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired
};

const Mosque = () => {
  const { id } = useParams();

  const mosqueColumns = [
    {
      accessor: 'mosque_name',
      Header: 'Mosque Name',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { mosque } = rowData.row.original;
        return <>{mosque?.name || '-'}</>;
      }
    },
    {
      accessor: 'mosque_address',
      Header: 'Location',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { mosque } = rowData.row.original;
        return <>{mosque?.address || '-'}</>;
      }
    },
    {
      accessor: 'start_date',
      Header: 'Start Date',
      Cell: rowData => {
        const { start_date } = rowData.row.original;
        return start_date ? format(new Date(start_date), 'dd/MM/yyyy') : '-';
      }
    },
    {
      accessor: 'end_date',
      Header: 'End Date',
      Cell: rowData => {
        const { end_date } = rowData.row.original;
        return end_date ? format(new Date(end_date), 'dd/MM/yyyy') : 'Present';
      }
    },
    {
      accessor: 'id_',
      Header: '',
      cellProps: {
        className: 'py-2 text-end'
      },
      disableSortBy: true,
      Cell: rowData => {
        const { id } = rowData.row.original;
        return (
          <TableRowAction
            isNotEdit
            onCLickDelete={() => handleDeleteClick(id)}
            subject={permissionsKeyMap.facilities.children.mosque}
          />
        );
      }
    }
  ];

  const reportColumns = [
    {
      accessor: 'reporter',
      Header: 'Reporter',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { user } = rowData.row.original;
        return <>{user ? user.first_name + ' ' + user.last_name : '-'}</>;
      }
    },
    {
      accessor: 'report_date',
      Header: 'Date',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { report_date } = rowData.row.original;
        return report_date ? dayjs(report_date).format('DD/MM/YYYY') : '-';
      }
    },
    {
      accessor: 'comment',
      Header: 'Comment',
      cellProps: {
        className: 'py-2',
        style: { maxWidth: '200px' }
      },
      Cell: rowData => {
        const { comment } = rowData.row.original;
        return (
          <span
            style={{
              display: 'block',
              maxWidth: '200px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
            title={comment || ''}
          >
            {comment || '-'}
          </span>
        );
      }
    },
    {
      accessor: 'report_name',
      Header: 'Report File',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { report_name, report_document } = rowData.row.original;
        return report_document ? (
          <ReportDocumentLink
            url={report_document}
            name={report_name || 'Report'}
          />
        ) : (
          'No File'
        );
      }
    },
    {
      accessor: 'id_',
      Header: '',
      cellProps: {
        className: 'py-2 text-end'
      },
      disableSortBy: true,
      Cell: rowData => {
        const { id } = rowData.row.original;
        return (
          <TableRowAction
            onCLickEdit={() => handleEditReportClick(rowData.row.original)}
            onCLickDelete={() => handleDeleteReportClick(id)}
            subject={permissionsKeyMap.sponsorship.children.beneficiaries}
          />
        );
      }
    }
  ];

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteReportModal, setShowDeleteReportModal] = useState(false);
  const [showEditReportModal, setShowEditReportModal] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [editObj, setEditObj] = useState(null);

  const [addMosqueModalShow, setAddMosqueModalShow] = useState(false);
  const [addReportModalShow, setAddReportModalShow] = useState(false);
  const { uploadDocument, loading: isUploadReportLoading } = useUpload();

  const { organization, selectedBranchId, user } = useAuthContext();

  // Use hooks for mosques
  const {
    beneficiaryMosques,
    isLoading: loading,
    refetch: refetchMosques
  } = useBeneficiaryMosques(id);
  const { createMosqueOrphan, isCreating: isAddMosqueLoading } =
    useCreateMosqueOrphan();
  const { deleteMosqueOrphan } = useDeleteMosqueOrphan();

  // Use hooks for mosque reports
  const {
    mosqueReports,
    isLoading: reportsLoading,
    refetch: refetchMosqueReports
  } = useMosqueReports(id);
  const { createMosqueReport, isCreating: isAddMosqueReportLoading } =
    useCreateMosqueReport();
  const { updateMosqueReport, isUpdating: isUpdateMosqueReportLoading } =
    useUpdateMosqueReport();
  const { deleteMosqueReport } = useDeleteMosqueReport();

  const handleDeleteClick = id => {
    setSelectedId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteReportClick = id => {
    setSelectedId(id);
    setShowDeleteReportModal(true);
  };

  const handleEditReportClick = obj => {
    setEditObj(obj);
    setShowEditReportModal(true);
  };

  const handleDeleteModalHide = () => {
    setSelectedId(null);
    setShowDeleteModal(false);
  };

  const handleDeleteReportModalHide = () => {
    setSelectedId(null);
    setShowDeleteReportModal(false);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteModal(false);
    deleteMosqueOrphan(selectedId);
    refetchMosques();
  };

  const handleConfirmDeleteReport = async () => {
    setShowDeleteReportModal(false);
    deleteMosqueReport(selectedId);
    refetchMosqueReports();
  };

  const submitAddMosque = async values => {
    createMosqueOrphan({
      mosque_id: values.mosque,
      orphan_id: id,
      branch_id: selectedBranchId,
      organisation_id: organization.id,
      user_id: user.id,
      start_date: values.startDate === '' ? null : values.startDate,
      end_date: values.endDate === '' ? null : values.endDate
    });
    setAddMosqueModalShow(false);
    refetchMosques();
  };

  const submitAddReport = async values => {
    const reportFile = values.reportDocument?.[0];
    let report_name;
    let report_document;

    if (reportFile && reportFile instanceof File) {
      report_name = reportFile.name.split('.')[0];
      try {
        report_document = await uploadDocument(reportFile, 'mosque-reports');
      } catch (err) {
        return;
      }
    }

    if (editObj) {
      updateMosqueReport({
        id: editObj.id,
        data: {
          mosque_id: values.mosque,
          comment: values.comment,
          reporter: values.reporter,
          user_id: user.id,
          report_date: values.date,
          report_name: report_name || values.editReport?.name || undefined,
          report_document:
            report_document || values.editReport?.url || undefined
        }
      });
      setEditObj(null);
      setShowEditReportModal(false);
    } else {
      createMosqueReport({
        branch_id: selectedBranchId,
        organisation_id: organization.id,
        orphan_id: id,
        mosque_id: values.mosque,
        user_id: user.id,
        reporter: values.reporter,
        comment: values.comment,
        report_date: values.date,
        report_name,
        report_document
      });
      setAddReportModalShow(false);
    }
    refetchMosqueReports();
  };

  const mosqueOptions = useMemo(() => {
    return (
      beneficiaryMosques?.map(item => ({
        label: item.mosque?.name,
        value: item.mosque?.id
      })) || []
    );
  }, [beneficiaryMosques]);

  return (
    <>
      <ConfirmationModal
        show={showDeleteModal || showDeleteReportModal}
        onHide={
          showDeleteModal ? handleDeleteModalHide : handleDeleteReportModalHide
        }
        title={showDeleteModal ? 'Remove Madrasah' : 'Delete Report'}
        handleConfirm={
          showDeleteModal ? handleConfirmDelete : handleConfirmDeleteReport
        }
        body={
          showDeleteModal
            ? 'Are you sure you want to remove selected Mosque?'
            : 'Are you sure you want to remove selected Report?'
        }
      />

      <Row>
        <Col md={12} xxl={12} className="h-md-100">
          <CustomAdvanceTable
            data={beneficiaryMosques || []}
            columns={mosqueColumns}
            title="Madrasahs"
            addButtonLabel="Add"
            onClickAddNew={() => {
              setAddMosqueModalShow(true);
            }}
            loading={loading}
            addNew
            subject={permissionsKeyMap.facilities.children.mosque}
          />
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={12} xxl={12} className="h-md-100">
          <CustomAdvanceTable
            data={mosqueReports || []}
            columns={reportColumns}
            title="Reports"
            addButtonLabel="Add"
            onClickAddNew={() => {
              setAddReportModalShow(true);
              setEditObj(null);
            }}
            loading={reportsLoading}
            addNew
            subject={permissionsKeyMap.facilities.children.mosque}
          />
        </Col>
      </Row>

      {addMosqueModalShow && (
        <AddMosqueModal
          show={addMosqueModalShow}
          onSubmit={submitAddMosque}
          isSubmitLoading={isAddMosqueLoading}
          onHide={() => {
            setAddMosqueModalShow(false);
          }}
          title="Add Mosque"
        />
      )}

      {(addReportModalShow || showEditReportModal) && (
        <AddReportModal
          show={addReportModalShow || showEditReportModal}
          onHide={() => {
            setAddReportModalShow(false);
            setShowEditReportModal(false);
          }}
          title={addReportModalShow ? 'Add Report' : 'Edit Report'}
          loading={
            isAddMosqueReportLoading ||
            isUpdateMosqueReportLoading ||
            isUploadReportLoading
          }
          onSubmit={submitAddReport}
          mosqueOptions={mosqueOptions}
          initValues={editObj}
        />
      )}
    </>
  );
};

export default Mosque;
